import React from "react";
import { ArrowUpRight, ShieldCheck } from "@phosphor-icons/react";

const CMF_LINK =
  "https://www.cmfchile.cl/institucional/mercados/entidad.php?auth=&send=&mercado=O&rut=96769070&grupo=&tipoentidad=RGEIN&vig=VI&row=AAAwy2ACTAAABy9AAY&control=svs&pestania=1";

const RAZON_SOCIAL = "EFE ARICA - LA PAZ S.A.";
const RUT_EMPRESA = "96.769.070-8";

/**
 * CMF registration footer — Chilean regulatory transparency block.
 * Two modes:
 *  - default (variant="light"): cream background for landing/marketing pages
 *  - variant="dark": forest background for in-app footer
 *  - variant="compact": single-row minimal footer for login/register
 */
export default function CMFFooter({ variant = "light" }) {
  if (variant === "compact") {
    return (
      <div className="border-t border-hairline bg-bg py-5" data-testid="cmf-footer-compact">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs font-mono text-inkMuted uppercase tracking-widest">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} weight="duotone" />
            <span>{RAZON_SOCIAL} · RUT {RUT_EMPRESA} · Registrada CMF</span>
          </div>
          <a
            href={CMF_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-forest hover:text-burnt normal-case tracking-normal text-sm"
            data-testid="cmf-link-compact"
          >
            Rectificar datos en CMF Chile <ArrowUpRight size={12} weight="bold" />
          </a>
        </div>
      </div>
    );
  }

  const isDark = variant === "dark";
  return (
    <footer
      data-testid="cmf-footer"
      className={`border-t ${isDark ? "bg-forest text-bg border-bg/10" : "bg-bg text-ink border-hairline"}`}
    >
      <div className="max-w-6xl mx-auto px-6 py-10 grid gap-8 md:grid-cols-[1.2fr_2fr_1fr]">
        <div>
          <div className={`label-mono mb-3 ${isDark ? "text-bg/60" : ""}`}>
            // Información CMF
          </div>
          <div className="font-display text-2xl font-bold tracking-tight leading-tight">
            Registro oficial<br />CMF Chile
          </div>
          <p className={`mt-3 text-sm ${isDark ? "text-bg/70" : "text-inkMuted"}`}>
            Entidad fiscalizada por la Comisión para el Mercado Financiero. Revisa y rectifica los datos en el registro oficial.
          </p>
          <a
            href={CMF_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cmf-link"
            className={`mt-5 inline-flex items-center gap-2 h-11 px-5 text-sm font-semibold ${
              isDark
                ? "bg-burnt text-bg hover:bg-burnt-hover"
                : "bg-forest text-bg hover:bg-forest-hover"
            }`}
          >
            Ver en CMF Chile <ArrowUpRight size={14} weight="bold" />
          </a>
        </div>

        <dl
          data-testid="cmf-data"
          className={`grid grid-cols-1 sm:grid-cols-2 gap-0 border ${
            isDark ? "border-bg/15" : "border-hairline"
          }`}
        >
          <Row label="RUT" value={RUT_EMPRESA} mono isDark={isDark} />
          <Row label="Vigencia" value="Vigente" isDark={isDark} right />
          <Row label="Razón social" value={RAZON_SOCIAL} isDark={isDark} top full />
          <Row label="Domicilio" value="Morandé 115, Piso 6 · Santiago · Metropolitana" isDark={isDark} top full />
          <Row label="N° de Inscripción" value="590" mono isDark={isDark} top />
          <Row label="Fecha de Inscripción" value="30/03/2021" mono isDark={isDark} top right />
          <Row label="Teléfono" value="225881" mono isDark={isDark} top />
          <Row label="Fax" value="231782" mono isDark={isDark} top right />
          <Row label="Email" value="contacto@efe-capital.lat" isDark={isDark} top />
          <Row label="Tipo entidad" value="RGEIN · Mercado O" mono isDark={isDark} top right />
        </dl>

        <div>
          <div className={`label-mono mb-3 ${isDark ? "text-bg/60" : ""}`}>// Atención</div>
          <div className="font-display text-2xl font-bold tracking-tight leading-tight mb-3">
            ¿Un dato incorrecto?
          </div>
          <p className={`text-sm ${isDark ? "text-bg/70" : "text-inkMuted"}`}>
            Si detectas información errónea en el registro, puedes solicitar su rectificación directamente en el portal oficial de la CMF.
          </p>
          <a
            href={CMF_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-4 inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-4 ${
              isDark ? "text-bg hover:text-burnt" : "text-forest hover:text-burnt"
            }`}
          >
            Ir al formulario de rectificación <ArrowUpRight size={12} weight="bold" />
          </a>
        </div>
      </div>

      <div
        className={`border-t ${
          isDark ? "border-bg/10" : "border-hairline"
        }`}
      >
        <div
          className={`max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs font-mono uppercase tracking-widest ${
            isDark ? "text-bg/60" : "text-inkMuted"
          }`}
        >
          <span>{RAZON_SOCIAL} · RUT {RUT_EMPRESA} · Simulación demo</span>
          <span>Santiago · Chile · 2026</span>
        </div>
      </div>
    </footer>
  );
}

function Row({ label, value, mono, isDark, top, right, full }) {
  const borderCls = isDark
    ? "border-bg/15"
    : "border-hairline";
  return (
    <div
      className={[
        "p-4",
        top ? `border-t ${borderCls}` : "",
        right ? `sm:border-l ${borderCls}` : "",
        full ? "sm:col-span-2" : "",
      ].join(" ")}
    >
      <div
        className={`label-mono ${isDark ? "text-bg/60" : ""}`}
        style={isDark ? { color: "rgba(247,246,242,0.6)" } : undefined}
      >
        {label}
      </div>
      <div className={`${mono ? "font-mono" : ""} text-sm font-medium mt-1`}>{value}</div>
    </div>
  );
}
